import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar, Clock, MapPin, Star, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = "https://zenvy-backend-e0zx.onrender.com";
const API = `${BACKEND_URL}/api`;

export default function CustomerDashboard() {
  const { user, getAuthHeader } = useAuth();
  const { setNeutralTheme } = useTheme();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming');

  useEffect(() => {
    setNeutralTheme();
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await fetch(`${API}/bookings`, {
        headers: getAuthHeader()
      });
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const upcomingBookings = bookings.filter(b => 
    ['pending', 'confirmed'].includes(b.status) && new Date(b.booking_date) >= new Date()
  );
  const pastBookings = bookings.filter(b => 
    ['completed', 'cancelled'].includes(b.status) || new Date(b.booking_date) < new Date()
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'in_service': return 'bg-purple-100 text-purple-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-zinc-100 text-zinc-800';
    }
  };

  const BookingCard = ({ booking }) => (
    <Card className="border-zinc-200" data-testid={`booking-card-${booking.booking_id}`}>
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="font-semibold text-lg text-zinc-900">{booking.salon_name}</h3>
              <Badge className={getStatusColor(booking.status)}>
                {booking.status.replace('_', ' ')}
              </Badge>
            </div>
            <p className="text-zinc-600 mb-3">{booking.service_name}</p>
            <div className="flex flex-wrap gap-4 text-sm text-zinc-500">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {booking.booking_date}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {booking.time_slot}
              </span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-zinc-900">${booking.total_price}</p>
            <p className="text-xs text-zinc-500">Booking ID: {booking.booking_id.slice(-8)}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-zinc-50">
      <Navbar />

      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-zinc-900" data-testid="dashboard-title">
            Welcome, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-zinc-600 mt-1">Manage your salon appointments</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="border-zinc-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Total Bookings</p>
                  <p className="text-3xl font-bold text-zinc-900">{bookings.length}</p>
                </div>
                <Calendar className="w-10 h-10 text-zinc-300" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-zinc-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Upcoming</p>
                  <p className="text-3xl font-bold text-blue-600">{upcomingBookings.length}</p>
                </div>
                <Clock className="w-10 h-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-zinc-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Completed</p>
                  <p className="text-3xl font-bold text-green-600">
                    {bookings.filter(b => b.status === 'completed').length}
                  </p>
                </div>
                <Star className="w-10 h-10 text-green-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Bookings Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="upcoming" data-testid="tab-upcoming">
              Upcoming ({upcomingBookings.length})
            </TabsTrigger>
            <TabsTrigger value="past" data-testid="tab-past">
              Past ({pastBookings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming">
            {loading ? (
              <div className="space-y-4">
                {[1, 2].map(i => (
                  <div key={i} className="h-32 bg-zinc-200 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : upcomingBookings.length > 0 ? (
              <div className="space-y-4" data-testid="upcoming-bookings">
                {upcomingBookings.map(booking => (
                  <BookingCard key={booking.booking_id} booking={booking} />
                ))}
              </div>
            ) : (
              <Card className="border-zinc-200">
                <CardContent className="p-12 text-center">
                  <AlertCircle className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-zinc-900 mb-2">No upcoming bookings</h3>
                  <p className="text-zinc-500 mb-4">Book your next salon appointment now!</p>
                  <Button onClick={() => window.location.href = '/salons'}>
                    Browse Salons
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="past">
            {loading ? (
              <div className="space-y-4">
                {[1, 2].map(i => (
                  <div key={i} className="h-32 bg-zinc-200 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : pastBookings.length > 0 ? (
              <div className="space-y-4" data-testid="past-bookings">
                {pastBookings.map(booking => (
                  <BookingCard key={booking.booking_id} booking={booking} />
                ))}
              </div>
            ) : (
              <Card className="border-zinc-200">
                <CardContent className="p-12 text-center">
                  <Calendar className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-zinc-900 mb-2">No past bookings</h3>
                  <p className="text-zinc-500">Your booking history will appear here</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Footer />
    </div>
  );
}
